import { Dispatch, PayloadAction, createSlice } from "@reduxjs/toolkit";
import { ErrorDetailContainer } from "../api/config";
import {
    CreateSaleInput,
    FilterSalesInput,
    ProjectSaleDto,
    SaleActivityDto,
    SaleDto,
    SaleService,
    SaleStatus,
    UpdateSaleInput,
    UserDto,
} from "../api/psc";
import {
    addOrUpdateSaleAction,
    addOrUpdateSaleActivityAction,
    deleteActivityAction,
    deleteSaleAction,
} from "./SharedActions";

export interface SalesState {
    isFetching: boolean;
    sales: ProjectSaleDto[];
    users: UserDto[];
    totalCount: number;
    limit: 18;
    page: number;
    filter: FilterSalesInput;
}

const initialState: SalesState = {
    isFetching: true,
    sales: [],
    users: [],
    totalCount: 0,
    limit: 18,
    page: 0,
    filter: {
        offset: 0,
        statuses: [],
        userIds: [],
        createDateFrom: null,
        createDateTo: null,
        term: "",
    },
};

const salesSlice = createSlice({
    name: "sales",
    initialState,
    reducers: {
        setSalesInitialState(
            state: SalesState,
            action: PayloadAction<{ currentUser: UserDto; users: UserDto[] }>
        ) {
            return {
                ...initialState,
                users: action.payload.users,
                filter: {
                    ...state.filter,
                    statuses: [SaleStatus.PENDING],
                    userIds: [action.payload.currentUser.id],
                },
            };
        },
        setSales(
            state: SalesState,
            action: PayloadAction<{
                sales: ProjectSaleDto[];
                totalCount: number;
            }>
        ) {
            return {
                ...state,
                sales: action.payload.sales,
                totalCount: action.payload.totalCount,
                isFetching: false,
            };
        },
        clearSales(state: SalesState, _action: PayloadAction) {
            return { ...state, isFetching: true };
        },
        setSalesPage(state: SalesState, action: PayloadAction<number>) {
            return {
                ...state,
                page: action.payload,
                filter: {
                    ...state.filter,
                    offset: action.payload * state.limit,
                },
            };
        },
        setSalesFilter(
            state: SalesState,
            action: PayloadAction<FilterSalesInput>
        ) {
            return {
                ...state,
                filter: action.payload,
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(
            addOrUpdateSaleAction,
            (state: SalesState, action: PayloadAction<SaleDto>) => {
                const updatedSales = [...state.sales];
                const saleToBeUpdated = updatedSales.find(
                    (a) => a.sale.id === action.payload.id
                );
                const index = updatedSales.indexOf(saleToBeUpdated);
                if (index !== -1) {
                    updatedSales[index] = {
                        ...updatedSales[index],
                        sale: action.payload,
                    };
                }
                return { ...state, sales: updatedSales };
            }
        );

        builder.addCase(
            deleteSaleAction,
            (state: SalesState, action: PayloadAction<number>) => {
                const updatedSales = [
                    ...state.sales.filter((s) => s.sale.id !== action.payload),
                ];
                return { ...state, sales: updatedSales };
            }
        );

        builder.addCase(
            addOrUpdateSaleActivityAction,
            (state: SalesState, action: PayloadAction<SaleActivityDto>) => {
                const updatedSales = [...state.sales];

                const sale = updatedSales.find(
                    (a) => a.sale.id === action.payload.saleId
                );
                const index = updatedSales.indexOf(sale);

                if (index !== -1) {
                    const updatedActivities = [...sale.sale.activities];
                    const activityToBeUpdated = updatedActivities.find(
                        (a) => a.id === action.payload.id
                    );
                    const indexOfActivity =
                        updatedActivities.indexOf(activityToBeUpdated);
                    if (indexOfActivity === -1) {
                        updatedActivities.push(action.payload);
                    } else {
                        updatedActivities[indexOfActivity] = action.payload;
                    }

                    const saleToBeUpdated = {
                        ...sale,
                        sale: { ...sale.sale, activities: updatedActivities },
                    };
                    updatedSales[index] = saleToBeUpdated;
                }

                return { ...state, sales: updatedSales };
            }
        );

        builder.addCase(
            deleteActivityAction,
            (state: SalesState, action: PayloadAction<number>) => {
                let updatedSales = [...state.sales];

                for (const sale of updatedSales) {
                    for (const activity of sale.sale.activities) {
                        if (activity.id === action.payload) {
                            const updatedActivities = [
                                ...sale.sale.activities.filter(
                                    (a) => a.id !== action.payload
                                ),
                            ];
                            const saleIndex = updatedSales.indexOf(sale);
                            updatedSales[saleIndex] = {
                                ...sale,
                                sale: {
                                    ...sale.sale,
                                    activities: updatedActivities,
                                },
                            };
                        }
                    }
                }

                return {
                    ...state,
                    sales: updatedSales,
                };
            }
        );
    },
});

export function resetAndFetchSales(state: SalesState, dispatch: Dispatch) {
    dispatch(clearSales());
    dispatch(setSalesPage(0));
    SaleService.listSales("1", { ...state.filter, offset: 0 }).then((r) => {
        dispatch(setSales({ sales: r.results, totalCount: r.count }));
    });
}

export function fetchSales(
    state: SalesState,
    dispatch: Dispatch,
    page: number
) {
    dispatch(clearSales());
    dispatch(setSalesPage(page));
    SaleService.listSales("1", {
        ...state.filter,
        offset: page * state.limit,
    }).then((r) => {
        dispatch(setSales({ sales: r.results, totalCount: r.count }));
    });
}

export async function createSaleThunk(
    sale: CreateSaleInput,
    _state: SalesState,
    dispatch: Dispatch
): Promise<{
    error?: ErrorDetailContainer;
}> {
    try {
        const r = await SaleService.createSale("1", sale);
        dispatch(addOrUpdateSaleAction(r));
        return {};
    } catch (e) {
        return { error: e as ErrorDetailContainer };
    }
}

export async function updateSaleThunk(
    saleId: number,
    sale: UpdateSaleInput,
    _state: SalesState,
    dispatch: Dispatch
): Promise<{
    error?: ErrorDetailContainer;
}> {
    try {
        const r = await SaleService.updateSale(saleId, "1", sale);
        dispatch(addOrUpdateSaleAction(r));
        return {};
    } catch (e) {
        return { error: e as ErrorDetailContainer };
    }
}

export async function deleteSaleThunk(
    saleId: number,
    _state: SalesState,
    dispatch: Dispatch
): Promise<any> {
    await SaleService.deleteSale(saleId, "1");
    dispatch(deleteSaleAction(saleId));
    return {};
}

export const {
    setSalesInitialState,
    setSales,
    setSalesFilter,
    clearSales,
    setSalesPage,
} = salesSlice.actions;
export default salesSlice.reducer;
